import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosPromise, AxiosRequestConfig } from "axios";
import objectHash from "object-hash";
import { useCallback, useMemo } from "react";

import HttpError from "../../../errors/HttpError";
import NetworkError from "../../../errors/NetworkError";
import { useApi } from "../SDKProvider";

export enum OrderingDirection {
    /** Ascending */
    ASC = "asc",
    /** Descending */
    DESC = "desc",
}

export interface IPaginateable {
    pagination?: false | { page: number; perPage: number };
}
export interface IOrderable<T> {
    order?: { by: T; direction: OrderingDirection };
}

export function mapOrder(order: IOrderable<unknown>["order"]) {
    return order && { [`order[${order.by}]`]: order.direction };
}

export function mapPagination(pagination: IPaginateable["pagination"]) {
    // Disable pagination on the query if not set
    return !pagination ? { pagination: false } : pagination;
}

export const unwrapAPIResult = async <T>(promise: AxiosPromise<T>) => {
    try {
        const res = await promise;

        return res.data;
    } catch (error) {
        if (error.response) {
            throw new HttpError(error?.response);
        } else {
            throw new NetworkError(error);
        }
    }
};

export interface IApiQueryFunction<T> {
    (data: T): AxiosRequestConfig | undefined;
}

export function createApiQueryHook<TQueryParameters, TQueryResult, TError = unknown>(
    queryFn: IApiQueryFunction<TQueryParameters>,
) {
    return (
        params: TQueryParameters,
        { enabled = true, queryKey, ...options }: UseQueryOptions<TQueryResult, TError> = {},
    ) => {
        const api = useApi();

        const { config, hash } = useMemo(() => {
            const config = queryFn(params);

            return {
                config,
                hash: config ? objectHash(config) : null,
            };
        }, [params]);

        const canEnable = !!config;

        const query = useCallback(
            async () => unwrapAPIResult(api.request<TQueryResult>(config as AxiosRequestConfig)),
            [api, hash],
        );

        return useQuery<TQueryResult, TError>({
            queryKey: queryKey ?? [hash],
            queryFn: query,
            enabled: canEnable && enabled,
            ...options,
        });
    };
}

export interface IApiMutationFunction<T> {
    (data: T): AxiosRequestConfig;
}

export function createApiMutationHook<TQueryParameters, TQueryResult, TError = HttpError | NetworkError>(
    mutationFn: IApiMutationFunction<TQueryParameters>,
) {
    return <TContext = unknown>(options: UseMutationOptions<TQueryResult, TError, TQueryParameters, TContext> = {}) => {
        const api = useApi();

        // Here we force typing to always be defined as we guarantee the fact that no calls will be emitted from react-query in this case
        const mutation = useCallback(
            async (params: TQueryParameters) => unwrapAPIResult(api.request<TQueryResult>(mutationFn(params))),
            [api],
        );

        return useMutation({
            mutationFn: mutation,
            ...options,
        });
    };
}
